// ABOUTME: Main API client for PhotoStream backend services
// ABOUTME: Handles gallery and photo operations with comprehensive error handling

import { Gallery, Photo, CreateGalleryRequest, ConfirmUploadRequest, GetUploadUrlRequest, UploadUrlResponse, PhotoSize, PhotoUrlResponse, PhotoStatusResponse } from './types';
import { PhotoStreamApiError, NetworkError } from './errors';
import { fetchAuthSession } from 'aws-amplify/auth';

export class PhotoStreamClient {
  constructor(private baseUrl: string) {}

  async createGallery(name: string, description: string): Promise<Gallery> {
    if (!name.trim()) {
      throw new Error('Name is required');
    }
    if (!description.trim()) {
      throw new Error('Description is required');
    }

    const request: CreateGalleryRequest = { name, description };
    return this.makeRequest('/galleries', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request)
    });
  }

  async listGalleries(): Promise<Gallery[]> {
    return this.makeRequest('/galleries');
  }

  async getGallery(galleryId: string): Promise<Gallery> {
    return this.makeRequest(`/galleries/${galleryId}`);
  }

  async getUploadUrl(galleryId: string, contentType: string): Promise<UploadUrlResponse> {
    const request: GetUploadUrlRequest = { contentType };
    return this.makeRequest(`/galleries/${galleryId}/photos/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request)
    });
  }

  async getPhotoStatus(galleryId: string, photoId: string): Promise<PhotoStatusResponse> {
    return this.makeRequest(`/galleries/${galleryId}/photos/${photoId}/status`);
  }

  async uploadToS3(uploadUrl: string, file: File): Promise<void> {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });

      if (!response.ok) {
        throw new PhotoStreamApiError(response.status, 'Failed to upload to S3');
      }
    } catch (error) {
      if (error instanceof PhotoStreamApiError) {
        throw error;
      }
      throw new NetworkError('Network error during S3 upload', error as Error);
    }
  }

  async confirmUpload(galleryId: string, s3Key: string): Promise<Photo> {
    const request: ConfirmUploadRequest = { s3Key };
    return this.makeRequest(`/galleries/${galleryId}/photos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request)
    });
  }

  async listPhotos(galleryId: string): Promise<Photo[]> {
    return this.makeRequest(`/galleries/${galleryId}/photos`);
  }

  async getPhotoViewUrl(s3Key: string, size?: PhotoSize): Promise<string> {
    const sizeParam = size ? `&size=${size}` : '';
    const response = await this.makeRequest<PhotoUrlResponse>(`/photo-url?key=${encodeURIComponent(s3Key)}${sizeParam}`);
    return response.viewUrl;
  }

  private async getAuthToken(): Promise<string | undefined> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.log('No auth session available');
      return undefined;
    }
  }

  private async makeRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
    try {
      const url = `${this.baseUrl}${endpoint}`;

      // Get auth token
      const token = await this.getAuthToken();
      console.log('Token available:', !!token, token ? `(${token.substring(0, 20)}...)` : '(none)');

      // Build headers
      const headers: Record<string, string> = {
        ...(options?.headers as Record<string, string> || {}),
      };

      // Add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('Authorization header added');
      } else {
        console.log('No token available - request will be unauthenticated');
      }

      const response = await fetch(url, {
        ...options,
        headers
      });

      if (!response.ok) {
        let errorMessage = 'Request failed';
        console.error('API request failed:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          headers: Object.fromEntries(response.headers.entries())
        });
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
          console.error('Error response body:', errorData);
        } catch (e) {
          console.error('Could not parse error response as JSON');
        }
        throw new PhotoStreamApiError(response.status, errorMessage);
      }

      try {
        return await response.json();
      } catch (error) {
        throw new Error('Failed to parse response');
      }
    } catch (error) {
      if (error instanceof PhotoStreamApiError) {
        throw error;
      }
      if (error instanceof Error && error.message === 'Failed to parse response') {
        throw error;
      }
      throw new NetworkError('Network error', error as Error);
    }
  }
}
